import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SalesSummaryEntity, SalesSummaryGroup, Timespan } from '../../../types';
import { startOfMonth, startOfWeek, startOfYear, subMonths, subWeeks, subYears } from 'date-fns';
import { Subject, forkJoin, of } from 'rxjs';
import { first } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DashboardKpiService } from '../../../services/dashboard-kpi.service';
import { isMultiDimensionProduct, Product, ProductMultiDimension, ProductService } from 'gung-standard';

type SalesSummarySortColumn = 'name' | 'totalQuantity' | 'totalAmount';
type SortDirection = 'asc' | 'desc';

@Component({
    selector: 'lib-sales-summary-card',
    templateUrl: './sales-summary-card.component.html',
    styleUrls: ['./sales-summary-card.component.scss'],
    standalone: false
})
export class SalesSummaryCardComponent implements OnInit, OnDestroy {
  protected unsubscribe: Subject<void> = new Subject();

  @Input()
  preConditions: any;

  salesSummaryEntity = SalesSummaryEntity;
  timespan = Timespan;
  currentGroupOn: SalesSummaryEntity;
  currentTimespan: Timespan;
  showPreviousTimeSpan = false;
  previousTimeSpanLabel: string = null;

  salesSummaryGroups: SalesSummaryGroup[];
  previousTimespanSalesSummaryGroups: { [id: string]: SalesSummaryGroup };
  allowProductDetails = false;
  showDetails = [];
  details = [];

  public displayedColumns: SalesSummarySortColumn[] = ['name', 'totalQuantity', 'totalAmount'];

  sortColumn: SalesSummarySortColumn = 'totalAmount';
  sortDirection: SortDirection = 'desc';
  sortIcons: { [key in SalesSummarySortColumn]: string } = {
    name: '',
    totalQuantity: '',
    totalAmount: 'fa-solid fa-arrow-down sort-arrow'
  };

  page = 1;
  pageSize = 7;
  collectionSize = 0;
  totalPages = 1;
  pageRangeLabel = '';
  pagedSalesSummaryGroups: SalesSummaryGroup[] = [];

  constructor(
    protected dashboardKpiService: DashboardKpiService,
    protected translateService: TranslateService,
    protected productService: ProductService
  ) {}

  ngOnInit() {
    this.currentGroupOn = SalesSummaryEntity.PRODUCT;
    this.currentTimespan = this.timespan.Year;

    this.fetchSalesSummaries();
  }

  protected fetchSalesSummaries() {
    const isToShowPreviousTimeSpan = this.getIsShowPreviousTimeSpan();

    const dateSpan = this.dashboardKpiService.getDateFromTimespan(this.currentTimespan);
    const previousDateSpan = this.dashboardKpiService.getDateFromTimespan(this.getPreviousTimeSpan());

    forkJoin({
      salesSummaryGroups: this.dashboardKpiService
        .getSalesSummary(this.currentGroupOn, dateSpan.fromDate, dateSpan.toDate, this.preConditions)
        .pipe(first()),
      previousSalesSummaryGroups: isToShowPreviousTimeSpan
        ? this.dashboardKpiService
            .getSalesSummary(
              this.currentGroupOn,
              previousDateSpan.fromDate,
              previousDateSpan.toDate,
              this.preConditions
            )
            .pipe(first())
        : of(null)
    }).subscribe(({ salesSummaryGroups, previousSalesSummaryGroups }) => {
      this.salesSummaryGroups = salesSummaryGroups;

      // If is to show the previous values
      // create the data map for the previous values
      // the map will contains the data for the ids in the salesSummaryGroups (which is the dataSource of the table)
      // and the goal is to get the previous values against each table row (current time span)
      if (isToShowPreviousTimeSpan) {
        this.previousTimespanSalesSummaryGroups = {};

        for (const salesSummaryGroup of salesSummaryGroups) {
          const previousValue = previousSalesSummaryGroups.find(previous => previous.id === salesSummaryGroup.id);
          this.showDetails[salesSummaryGroup.id] = false;
          this.details[salesSummaryGroup.id] = { color: 'UNDEFINED', size: 'UNDEFINED', img: 'UNDEFINED' };
          /*  this.details[salesSummaryGroup.id] = {color: '', size: '', img: ''}; */
          if (!!previousValue) {
            this.previousTimespanSalesSummaryGroups[salesSummaryGroup.id] = previousValue;
          } else {
            this.previousTimespanSalesSummaryGroups[salesSummaryGroup.id] = {
              ...salesSummaryGroup,
              totalAmount: 0,
              totalQuantity: 0
            };
          }
        }
      }

      this.showPreviousTimeSpan = isToShowPreviousTimeSpan;
      this.previousTimeSpanLabel = this.computePreviousTimeSpanLabel();

      this.page = 1;
      this.applySort();
    });
  }

  seeDetails(product: SalesSummaryGroup) {
    this.showDetails[product.id] = true;
    this.productService
      .getProduct(product.id)
      .pipe(first())
      .subscribe((data: ProductMultiDimension) => {
        let img = '';
        if (data.extra.images && data.extra.images[0]) {
          img = data.extra.images[0].s3Uri;
        }
        if (isMultiDimensionProduct(data)) {
          this.details[data.id] = { color: data.primaryDimension[0].name, size: data.secondaryDimension[0].name, img };
        } else {
          this.details[data.id] = { color: 'UNDEFINED', size: 'UNDEFINED', img };
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  chooseGroup(entity: SalesSummaryEntity) {
    this.currentGroupOn = entity;
    this.fetchSalesSummaries();
  }

  changeTimespan(newTimespan: Timespan) {
    this.currentTimespan = newTimespan;
    this.fetchSalesSummaries();
  }

  sortBy(column: SalesSummarySortColumn) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.page = 1;
    this.applySort();
  }

  onPageChange(page: number) {
    this.page = page;
    this.applyPaging();
  }

  goToPreviousPage() {
    if (this.page > 1) {
      this.onPageChange(this.page - 1);
    }
  }

  goToNextPage() {
    if (this.page < this.totalPages) {
      this.onPageChange(this.page + 1);
    }
  }

  protected applySort() {
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    const column = this.sortColumn;

    this.salesSummaryGroups = [...(this.salesSummaryGroups || [])].sort((a, b) => {
      if (a[column] < b[column]) {
        return -1 * direction;
      }
      if (a[column] > b[column]) {
        return 1 * direction;
      }
      return 0;
    });

    this.sortIcons = {
      name: this.getSortIconClass('name'),
      totalQuantity: this.getSortIconClass('totalQuantity'),
      totalAmount: this.getSortIconClass('totalAmount')
    };

    this.collectionSize = this.salesSummaryGroups.length;
    this.applyPaging();
  }

  protected getSortIconClass(column: SalesSummarySortColumn): string {
    if (this.sortColumn !== column) {
      return '';
    }
    return this.sortDirection === 'asc' ? 'fa-solid fa-arrow-down sort-arrow rotated' : 'fa-solid fa-arrow-down sort-arrow';
  }

  protected applyPaging() {
    this.totalPages = Math.max(1, Math.ceil(this.collectionSize / this.pageSize));
    if (this.page > this.totalPages) {
      this.page = this.totalPages;
    }

    const start = (this.page - 1) * this.pageSize;
    this.pagedSalesSummaryGroups = (this.salesSummaryGroups || []).slice(start, start + this.pageSize);

    const rangeStart = this.collectionSize === 0 ? 0 : start + 1;
    const rangeEnd = Math.min(start + this.pageSize, this.collectionSize);
    this.pageRangeLabel = `${rangeStart} – ${rangeEnd} of ${this.collectionSize}`;
  }

  protected computePreviousTimeSpanLabel(): string {
    switch (this.currentTimespan) {
      case Timespan.Year:
        return this.translateService.instant('LAST_YEAR');

      case Timespan.Month:
        return this.translateService.instant('LAST_MONTH');

      case Timespan.Week:
        return this.translateService.instant('LAST_WEEK');
    }

    return null;
  }

  getPreviousTimeSpan(): Timespan {
    switch (this.currentTimespan) {
      case Timespan.Year:
        return Timespan.LastYear;

      case Timespan.Month:
        return Timespan.LastMonth;

      case Timespan.Week:
        return Timespan.LastWeek;
    }

    return null;
  }

  protected getIsShowPreviousTimeSpan(): boolean {
    return (
      this.currentTimespan === Timespan.Year ||
      this.currentTimespan === Timespan.Week ||
      this.currentTimespan === Timespan.Month
    );
  }
}
